import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Side = "right" | "left" | "top" | "bottom";

interface TooltipProps {
  label: string;
  side?: Side;
  delayMs?: number;
  children: React.ReactElement;
  disabled?: boolean;
}

interface Position {
  top: number;
  left: number;
}

const GAP = 8;

const computePosition = (
  rect: DOMRect,
  side: Side,
  tooltipW: number,
  tooltipH: number
): Position => {
  switch (side) {
    case "right":
      return {
        top: rect.top + rect.height / 2 - tooltipH / 2,
        left: rect.right + GAP,
      };
    case "left":
      return {
        top: rect.top + rect.height / 2 - tooltipH / 2,
        left: rect.left - tooltipW - GAP,
      };
    case "top":
      return {
        top: rect.top - tooltipH - GAP,
        left: rect.left + rect.width / 2 - tooltipW / 2,
      };
    case "bottom":
      return {
        top: rect.bottom + GAP,
        left: rect.left + rect.width / 2 - tooltipW / 2,
      };
  }
};

const Tooltip: React.FC<TooltipProps> = ({
  label,
  side = "right",
  delayMs = 200,
  disabled = false,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleEnter = () => {
    if (disabled || !label) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => setOpen(true), delayMs);
  };

  const handleLeave = () => {
    clearTimer();
    setOpen(false);
  };

  // Position the tooltip after it mounts so we can measure its real size and
  // center it relative to the trigger. Re-measure on label change.
  useEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const tip = tooltipRef.current.getBoundingClientRect();
    setPos(computePosition(trigger, side, tip.width, tip.height));
  }, [open, side, label]);

  useEffect(() => () => clearTimer(), []);

  // Clone the child to attach event handlers + a ref. Preserves the child's
  // own ref (if any) and merges handlers without clobbering existing ones.
  const child = React.Children.only(children);
  const cloned = React.cloneElement(child, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const childRef = (child as any).ref;
      if (typeof childRef === "function") childRef(node);
      else if (childRef && typeof childRef === "object") childRef.current = node;
    },
    onMouseEnter: (e: React.MouseEvent) => {
      child.props.onMouseEnter?.(e);
      handleEnter();
    },
    onMouseLeave: (e: React.MouseEvent) => {
      child.props.onMouseLeave?.(e);
      handleLeave();
    },
    onFocus: (e: React.FocusEvent) => {
      child.props.onFocus?.(e);
      handleEnter();
    },
    onBlur: (e: React.FocusEvent) => {
      child.props.onBlur?.(e);
      handleLeave();
    },
  });

  return (
    <>
      {cloned}
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className="fixed z-[60] pointer-events-none px-2 py-1 text-xs font-medium rounded-md bg-surface-overlay text-primary border border-border-default shadow-md whitespace-nowrap"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {label}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
